import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Mail, Users, Megaphone, CheckSquare, DollarSign, ShoppingCart, Database, Code, Phone, Search, LayoutGrid, Globe, BarChart3, Sparkles, Briefcase, Palette } from 'lucide-react';
const categories = [{
  title: 'Communication',
  icon: <MessageSquare className="w-5 h-5" data-id="element-1775" />,
  tools: [{
    name: 'Slack',
    desc: 'MR reads channels, replies, and creates tasks.',
    domain: 'slack.com'
  }, {
    name: 'Microsoft Teams',
    desc: 'Full integration with Teams chats and channels.',
    domain: 'microsoft.com'
  }, {
    name: 'Discord',
    desc: 'Community management and automated moderation.',
    domain: 'discord.com'
  }, {
    name: 'Intercom',
    desc: 'Drafts and sends customer support replies.',
    domain: 'intercom.com'
  }, {
    name: 'Zendesk',
    desc: 'Resolves tickets and updates customer records.',
    domain: 'zendesk.com'
  }, {
    name: 'Telegram',
    desc: 'Automates messages and manages group chats.',
    domain: 'telegram.org'
  }, {
    name: 'WhatsApp',
    desc: 'Sends notifications and handles customer inquiries.',
    domain: 'whatsapp.com'
  }, {
    name: 'Crisp',
    desc: 'Manages live chat and automated responses.',
    domain: 'crisp.chat'
  }, {
    name: 'LiveChat',
    desc: 'Engages visitors and routes support tickets.',
    domain: 'livechat.com'
  }]
}, {
  title: 'Email & Calendar',
  icon: <Mail className="w-5 h-5" data-id="element-1776" />,
  tools: [{
    name: 'Gmail',
    desc: 'Reads inbox, drafts replies, categorizes emails.',
    domain: 'gmail.com'
  }, {
    name: 'Outlook',
    desc: 'Full email and calendar management.',
    domain: 'outlook.com'
  }, {
    name: 'Calendly',
    desc: 'Schedules meetings and handles rescheduling.',
    domain: 'calendly.com'
  }, {
    name: 'Google Calendar',
    desc: 'Manages your schedule and resolves conflicts.',
    domain: 'google.com'
  }, {
    name: 'SendGrid',
    desc: 'Sends transactional emails and tracks delivery.',
    domain: 'sendgrid.com'
  }, {
    name: 'Mailgun',
    desc: 'Automates email routing and parsing.',
    domain: 'mailgun.com'
  }, {
    name: 'Yahoo Mail',
    desc: 'Syncs inbox and manages email workflows.',
    domain: 'yahoo.com'
  }]
}, {
  title: 'CRM & Sales',
  icon: <Users className="w-5 h-5" data-id="element-1777" />,
  tools: [{
    name: 'Salesforce',
    desc: 'Updates records, logs calls, moves deal stages.',
    domain: 'salesforce.com'
  }, {
    name: 'HubSpot',
    desc: 'Full two-way sync for contacts and deals.',
    domain: 'hubspot.com'
  }, {
    name: 'Pipedrive',
    desc: 'Automates pipeline management and follow-ups.',
    domain: 'pipedrive.com'
  }, {
    name: 'Close',
    desc: 'Logs activities and updates lead statuses.',
    domain: 'close.com'
  }, {
    name: 'Zoho CRM',
    desc: 'Manages leads, accounts, and sales pipelines.',
    domain: 'zoho.com'
  }, {
    name: 'Freshsales',
    desc: 'Tracks engagement and automates sales tasks.',
    domain: 'freshworks.com'
  }, {
    name: 'Copper',
    desc: 'Syncs Google Workspace data to CRM records.',
    domain: 'copper.com'
  }, {
    name: 'Keap',
    desc: 'Automates follow-ups and client communications.',
    domain: 'keap.com'
  }, {
    name: '1Dash Pro',
    desc: 'Unified dashboard for client management and reporting.',
    domain: '1dash.pro'
  }, {
    name: 'GoHighLevel',
    desc: 'All-in-one CRM, funnels, and marketing automation.',
    domain: 'gohighlevel.com'
  }]
}, {
  title: 'Marketing',
  icon: <Megaphone className="w-5 h-5" data-id="element-1778" />,
  tools: [{
    name: 'Google Ads',
    desc: 'Monitors spend and adjusts campaign bids.',
    domain: 'google.com'
  }, {
    name: 'Meta Ads',
    desc: 'Tracks performance and pauses losing ads.',
    domain: 'meta.com'
  }, {
    name: 'Mailchimp',
    desc: 'Manages lists and drafts newsletter campaigns.',
    domain: 'mailchimp.com'
  }, {
    name: 'ActiveCampaign',
    desc: 'Triggers complex automation sequences.',
    domain: 'activecampaign.com'
  }, {
    name: 'Constant Contact',
    desc: 'Syncs subscribers and sends broadcasts.',
    domain: 'constantcontact.com'
  }, {
    name: 'Semrush',
    desc: 'Tracks keyword rankings and SEO performance.',
    domain: 'semrush.com'
  }, {
    name: 'Hootsuite',
    desc: 'Schedules posts and monitors social mentions.',
    domain: 'hootsuite.com'
  }, {
    name: 'Buffer',
    desc: 'Automates social media publishing and analytics.',
    domain: 'buffer.com'
  }, {
    name: 'Kit',
    desc: 'Manages creator email lists and automations.',
    domain: 'kit.com'
  }, {
    name: 'Klaviyo',
    desc: 'Triggers e-commerce email and SMS flows.',
    domain: 'klaviyo.com'
  }, {
    name: 'Brevo',
    desc: 'Sends marketing campaigns and transactional messages.',
    domain: 'brevo.com'
  }]
}, {
  title: 'Project Management',
  icon: <CheckSquare className="w-5 h-5" data-id="element-1779" />,
  tools: [{
    name: 'ClickUp',
    desc: 'Creates tasks, updates statuses, assigns team.',
    domain: 'clickup.com'
  }, {
    name: 'Asana',
    desc: 'Full project tracking and milestone updates.',
    domain: 'asana.com'
  }, {
    name: 'Monday',
    desc: 'Updates boards and notifies stakeholders.',
    domain: 'monday.com'
  }, {
    name: 'Linear',
    desc: 'Creates issues and tracks engineering sprints.',
    domain: 'linear.app'
  }, {
    name: 'Notion',
    desc: 'Updates databases and writes documentation.',
    domain: 'notion.so'
  }, {
    name: 'Trello',
    desc: 'Moves cards and adds detailed descriptions.',
    domain: 'trello.com'
  }, {
    name: 'Basecamp',
    desc: 'Manages projects, messages, and to-dos.',
    domain: 'basecamp.com'
  }, {
    name: 'Jira',
    desc: 'Tracks bugs, issues, and agile workflows.',
    domain: 'atlassian.com'
  }, {
    name: 'Wrike',
    desc: 'Automates project planning and resource management.',
    domain: 'wrike.com'
  }, {
    name: 'Smartsheet',
    desc: 'Updates grid data and automates workflows.',
    domain: 'smartsheet.com'
  }]
}, {
  title: 'Finance',
  icon: <DollarSign className="w-5 h-5" data-id="element-1780" />,
  tools: [{
    name: 'Stripe',
    desc: 'Tracks payments, handles refunds, flags disputes.',
    domain: 'stripe.com'
  }, {
    name: 'QuickBooks',
    desc: 'Categorizes expenses and drafts invoices.',
    domain: 'quickbooks.intuit.com'
  }, {
    name: 'Xero',
    desc: 'Reconciles transactions and tracks cash flow.',
    domain: 'xero.com'
  }, {
    name: 'FreshBooks',
    desc: 'Manages client billing and time tracking.',
    domain: 'freshbooks.com'
  }, {
    name: 'Square',
    desc: 'Syncs point-of-sale data and inventory.',
    domain: 'squareup.com'
  }, {
    name: 'Wave',
    desc: 'Automates accounting and receipt scanning.',
    domain: 'waveapps.com'
  }, {
    name: 'PayPal',
    desc: 'Processes transactions and manages payouts.',
    domain: 'paypal.com'
  }, {
    name: 'Brex',
    desc: 'Tracks corporate card spend and expenses.',
    domain: 'brex.com'
  }, {
    name: 'Plaid',
    desc: 'Connects bank accounts and verifies balances.',
    domain: 'plaid.com'
  }]
}, {
  title: 'E-Commerce',
  icon: <ShoppingCart className="w-5 h-5" data-id="element-1781" />,
  tools: [{
    name: 'Shopify',
    desc: 'Tracks orders, updates inventory, handles support.',
    domain: 'shopify.com'
  }, {
    name: 'WooCommerce',
    desc: 'Syncs products and manages customer data.',
    domain: 'woocommerce.com'
  }, {
    name: 'BigCommerce',
    desc: 'Automates order fulfillment workflows.',
    domain: 'bigcommerce.com'
  }, {
    name: 'Amazon',
    desc: 'Manages seller central orders and inventory.',
    domain: 'amazon.com'
  }, {
    name: 'Etsy',
    desc: 'Syncs shop listings and customer messages.',
    domain: 'etsy.com'
  }, {
    name: 'eBay',
    desc: 'Automates auction listings and order tracking.',
    domain: 'ebay.com'
  }, {
    name: 'Adobe Commerce',
    desc: 'Integrates enterprise e-commerce data.',
    domain: 'adobe.com'
  }]
}, {
  title: 'Data & Storage',
  icon: <Database className="w-5 h-5" data-id="element-1782" />,
  tools: [{
    name: 'Airtable',
    desc: 'Reads and writes complex relational data.',
    domain: 'airtable.com'
  }, {
    name: 'Google Sheets',
    desc: 'Updates rows and generates reports.',
    domain: 'google.com'
  }, {
    name: 'Google Drive',
    desc: 'Organizes files and searches documents.',
    domain: 'google.com'
  }, {
    name: 'Dropbox',
    desc: 'Syncs assets and manages folder structures.',
    domain: 'dropbox.com'
  }, {
    name: 'Snowflake',
    desc: 'Queries data warehouse and runs analytics.',
    domain: 'snowflake.com'
  }, {
    name: 'Supabase',
    desc: 'Manages database records and user auth.',
    domain: 'supabase.com'
  }, {
    name: 'Firebase',
    desc: 'Syncs real-time database and cloud storage.',
    domain: 'firebase.google.com'
  }, {
    name: 'MongoDB',
    desc: 'Reads and writes NoSQL document data.',
    domain: 'mongodb.com'
  }, {
    name: 'OneDrive',
    desc: 'Automates file sharing and cloud backups.',
    domain: 'microsoft.com'
  }]
}, {
  title: 'Developer',
  icon: <Code className="w-5 h-5" data-id="element-1783" />,
  tools: [{
    name: 'GitHub',
    desc: 'Reviews PRs, creates issues, tracks commits.',
    domain: 'github.com'
  }, {
    name: 'GitLab',
    desc: 'Manages CI/CD pipelines and code reviews.',
    domain: 'gitlab.com'
  }, {
    name: 'Zapier',
    desc: 'Connects to 5,000+ additional applications.',
    domain: 'zapier.com'
  }, {
    name: 'Make',
    desc: 'Triggers complex multi-step scenarios.',
    domain: 'make.com'
  }, {
    name: 'Bitbucket',
    desc: 'Manages repositories and pull requests.',
    domain: 'bitbucket.org'
  }, {
    name: 'Vercel',
    desc: 'Triggers deployments and monitors build status.',
    domain: 'vercel.com'
  }, {
    name: 'Netlify',
    desc: 'Automates site builds and form submissions.',
    domain: 'netlify.com'
  }, {
    name: 'Postman',
    desc: 'Runs API tests and monitors endpoints.',
    domain: 'postman.com'
  }, {
    name: 'Sentry',
    desc: 'Tracks errors and creates bug tickets.',
    domain: 'sentry.io'
  }]
}, {
  title: 'Voice & Phone',
  icon: <Phone className="w-5 h-5" data-id="element-1784" />,
  tools: [{
    name: 'Twilio',
    desc: 'Sends SMS and routes inbound phone calls.',
    domain: 'twilio.com'
  }, {
    name: 'Vapi',
    desc: 'Powers conversational AI voice agents.',
    domain: 'vapi.ai'
  }, {
    name: 'RingCentral',
    desc: 'Logs call transcripts and voicemails.',
    domain: 'ringcentral.com'
  }, {
    name: 'Vonage',
    desc: 'Automates voice APIs and communication flows.',
    domain: 'vonage.com'
  }, {
    name: 'Dialpad',
    desc: 'Transcribes calls and logs meeting notes.',
    domain: 'dialpad.com'
  }, {
    name: 'Aircall',
    desc: 'Syncs call data to CRM and helpdesk.',
    domain: 'aircall.io'
  }]
}, {
  title: 'Social Media',
  icon: <Globe className="w-5 h-5" data-id="element-1785" />,
  tools: [{
    name: 'Facebook',
    desc: 'Manages pages, posts, and messenger bots.',
    domain: 'facebook.com'
  }, {
    name: 'Instagram',
    desc: 'Schedules posts and replies to DMs.',
    domain: 'instagram.com'
  }, {
    name: 'LinkedIn',
    desc: 'Automates outreach and company updates.',
    domain: 'linkedin.com'
  }, {
    name: 'Twitter/X',
    desc: 'Monitors mentions and schedules tweets.',
    domain: 'x.com'
  }, {
    name: 'TikTok',
    desc: 'Tracks video performance and trends.',
    domain: 'tiktok.com'
  }, {
    name: 'Pinterest',
    desc: 'Automates pin creation and board management.',
    domain: 'pinterest.com'
  }, {
    name: 'YouTube',
    desc: 'Uploads videos and moderates comments.',
    domain: 'youtube.com'
  }]
}, {
  title: 'Analytics',
  icon: <BarChart3 className="w-5 h-5" data-id="element-1786" />,
  tools: [{
    name: 'Google Analytics',
    desc: 'Tracks website traffic and user behavior.',
    domain: 'google.com'
  }, {
    name: 'Mixpanel',
    desc: 'Analyzes product usage and user funnels.',
    domain: 'mixpanel.com'
  }, {
    name: 'Amplitude',
    desc: 'Tracks events and customer journeys.',
    domain: 'amplitude.com'
  }, {
    name: 'Hotjar',
    desc: 'Records sessions and collects user feedback.',
    domain: 'hotjar.com'
  }, {
    name: 'Segment',
    desc: 'Routes customer data to multiple destinations.',
    domain: 'segment.com'
  }, {
    name: 'Datadog',
    desc: 'Monitors infrastructure and application performance.',
    domain: 'datadoghq.com'
  }]
}, {
  title: 'AI & Automation',
  icon: <Sparkles className="w-5 h-5" data-id="element-1787" />,
  tools: [{
    name: 'OpenAI',
    desc: 'Generates text, code, and images via API.',
    domain: 'openai.com'
  }, {
    name: 'Anthropic',
    desc: 'Powers conversational agents with Claude.',
    domain: 'anthropic.com'
  }, {
    name: 'ElevenLabs',
    desc: 'Generates realistic AI voiceovers.',
    domain: 'elevenlabs.io'
  }, {
    name: 'Replicate',
    desc: 'Runs open-source machine learning models.',
    domain: 'replicate.com'
  }, {
    name: 'Hugging Face',
    desc: 'Accesses thousands of pre-trained AI models.',
    domain: 'huggingface.co'
  }]
}, {
  title: 'HR & Recruiting',
  icon: <Briefcase className="w-5 h-5" data-id="element-1788" />,
  tools: [{
    name: 'BambooHR',
    desc: 'Manages employee records and time off.',
    domain: 'bamboohr.com'
  }, {
    name: 'Gusto',
    desc: 'Automates payroll and benefits administration.',
    domain: 'gusto.com'
  }, {
    name: 'Lever',
    desc: 'Tracks applicants and schedules interviews.',
    domain: 'lever.co'
  }, {
    name: 'Greenhouse',
    desc: 'Manages hiring pipelines and scorecards.',
    domain: 'greenhouse.io'
  }, {
    name: 'Workday',
    desc: 'Integrates enterprise HR and finance data.',
    domain: 'workday.com'
  }]
}, {
  title: 'Design',
  icon: <Palette className="w-5 h-5" data-id="element-1789" />,
  tools: [{
    name: 'Figma',
    desc: 'Syncs design files and extracts assets.',
    domain: 'figma.com'
  }, {
    name: 'Canva',
    desc: 'Automates template creation and publishing.',
    domain: 'canva.com'
  }, {
    name: 'Adobe',
    desc: 'Integrates Creative Cloud workflows.',
    domain: 'adobe.com'
  }, {
    name: 'Miro',
    desc: 'Updates whiteboards and visual workspaces.',
    domain: 'miro.com'
  }]
}];
const floatingLogos = [
// Left column — far left edge
{
  domain: 'slack.com',
  top: '5%',
  left: '3%',
  size: 56,
  delay: 0
}, {
  domain: 'mailchimp.com',
  top: '22%',
  left: '6%',
  size: 56,
  delay: 1.6
}, {
  domain: 'asana.com',
  top: '40%',
  left: '2%',
  size: 48,
  delay: 0.9
}, {
  domain: 'figma.com',
  top: '58%',
  left: '5%',
  size: 48,
  delay: 0.6
}, {
  domain: 'gmail.com',
  top: '75%',
  left: '3%',
  size: 64,
  delay: 1
},
// Left column — second ring
{
  domain: 'stripe.com',
  top: '8%',
  left: '14%',
  size: 48,
  delay: 0.2
}, {
  domain: 'shopify.com',
  top: '30%',
  left: '12%',
  size: 56,
  delay: 0.3
}, {
  domain: 'dropbox.com',
  top: '52%',
  left: '13%',
  size: 50,
  delay: 0.8
}, {
  domain: 'linear.app',
  top: '85%',
  left: '10%',
  size: 48,
  delay: 1.2
},
// Right column — far right edge
{
  domain: 'hubspot.com',
  top: '5%',
  right: '3%',
  size: 64,
  delay: 0.5
}, {
  domain: 'discord.com',
  top: '24%',
  right: '5%',
  size: 52,
  delay: 1.3
}, {
  domain: 'zendesk.com',
  top: '42%',
  right: '2%',
  size: 50,
  delay: 1.1
}, {
  domain: 'openai.com',
  top: '60%',
  right: '4%',
  size: 60,
  delay: 0.4
}, {
  domain: 'calendly.com',
  top: '80%',
  right: '3%',
  size: 54,
  delay: 1.4
},
// Right column — second ring
{
  domain: 'salesforce.com',
  top: '12%',
  right: '14%',
  size: 52,
  delay: 0.8
}, {
  domain: 'twilio.com',
  top: '34%',
  right: '12%',
  size: 44,
  delay: 1.1
}, {
  domain: 'trello.com',
  top: '52%',
  right: '13%',
  size: 54,
  delay: 0.5
}, {
  domain: 'notion.so',
  top: '70%',
  right: '11%',
  size: 56,
  delay: 1.5
}, {
  domain: 'github.com',
  top: '88%',
  right: '14%',
  size: 56,
  delay: 0.7
}, {
  domain: 'zapier.com',
  top: '90%',
  left: '6%',
  size: 58,
  delay: 1.2
}];
export function Integrations() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All Integrations');
  // Flatten all tools and inject their category name
  const allTools = useMemo(() => {
    return categories.flatMap(cat => cat.tools.map(tool => ({
      ...tool,
      category: cat.title
    })));
  }, []);
  // Filter tools based on search and active category
  const filteredTools = useMemo(() => {
    return allTools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(searchQuery.toLowerCase()) || tool.desc.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All Integrations' || tool.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [allTools, searchQuery, activeCategory]);
  return <main className="pt-[140px] min-h-screen bg-off-white" data-id="element-1790">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 px-6 overflow-hidden bg-white border-b border-gray-100" data-id="element-1791">
        {/* Floating Logos (Desktop Only) */}
        {floatingLogos.map((logo, i) => <motion.div key={i} className="absolute hidden lg:flex items-center justify-center bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 overflow-hidden z-0" style={{
        top: logo.top,
        left: logo.left,
        right: logo.right,
        width: logo.size,
        height: logo.size
      }} animate={{
        y: [0, -15, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        delay: logo.delay,
        ease: 'easeInOut'
      }} data-id="element-1792">
            <img src={`https://logo.clearbit.com/${logo.domain}`} alt={`${logo.domain} logo`} className="w-3/5 h-3/5 object-contain" onError={e => {
          const target = e.target as HTMLImageElement;
          if (!target.dataset.fallback) {
            target.dataset.fallback = '1';
            target.src = `https://www.google.com/s2/favicons?domain=${logo.domain}&sz=128`;
          } else {
            target.style.display = 'none';
          }
        }} data-id="element-1793" />
          </motion.div>)}

        <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-1794">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1795">
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl text-white leading-[1.1] mb-6" data-id="element-1796">
              MISTR Connects to <br data-id="element-1797" />
              <span className="text-teal" data-id="element-1798">Your Entire World.</span>
            </h1>
            <p className="text-lg md:text-xl text-steel mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-1799">
              MISTR seamlessly integrates with {allTools.length}+ of the tools
              you already use. No complex setup, no Zapier required. Just
              connect and let MR get to work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-[1400px] mx-auto px-6 py-12 md:py-20" data-id="element-1800">
        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-16 relative z-20" data-id="element-1801">
          <div className="relative flex items-center" data-id="element-1802">
            <Search className="absolute left-4 text-gray-400" size={24} data-id="element-1803" />
            <input type="text" placeholder="Search integrations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-14 pr-6 py-4 rounded-2xl border border-gray-200 bg-white shadow-sm text-lg focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal transition-all placeholder:text-gray-400 text-gray-900" data-id="element-1804" />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16" data-id="element-1805">
          {/* Left Sidebar - Categories */}
          <aside className="w-full lg:w-64 shrink-0" data-id="element-1806">
            <div className="sticky top-[160px] flex flex-col gap-1" data-id="element-1807">
              <button onClick={() => setActiveCategory('All Integrations')} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${activeCategory === 'All Integrations' ? 'bg-teal/10 text-teal font-semibold' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-1808">
                <LayoutGrid className="w-5 h-5" data-id="element-1809" />
                All Integrations
              </button>

              {categories.map((cat, idx) => <button key={idx} onClick={() => setActiveCategory(cat.title)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${activeCategory === cat.title ? 'bg-teal/10 text-teal font-semibold' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-1810">
                  {cat.icon}
                  {cat.title}
                </button>)}
            </div>
          </aside>

          {/* Right Main Area - Grid */}
          <div className="flex-1" data-id="element-1811">
            {filteredTools.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-id="element-1812">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1813">
                  <Search className="text-gray-400" size={28} data-id="element-1814" />
                </div>
                <h3 className="text-xl font-poppins font-semibold text-gray-900 mb-2" data-id="element-1815">
                  No integrations found
                </h3>
                <p className="text-gray-500" data-id="element-1816">
                  We couldn't find any tools matching "{searchQuery}".
                </p>
                <button onClick={() => setSearchQuery('')} className="mt-6 text-teal font-medium hover:text-teal/80 transition-colors" data-id="element-1817">
                  Clear search
                </button>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-1818">
                {filteredTools.map((tool, idx) => <motion.div key={`${tool.name}-${idx}`} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: Math.min(idx * 0.05, 0.3)
            }} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:-translate-y-1 hover:border-teal/30 transition-all group flex flex-col" data-id="element-1819">
                    <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-5 p-2.5 group-hover:bg-white transition-colors overflow-hidden" data-id="element-1820">
                      <img src={`https://logo.clearbit.com/${tool.domain}`} alt={`${tool.name} logo`} className="w-full h-full object-contain" onError={e => {
                  const target = e.target as HTMLImageElement;
                  // Try Google favicon as fallback
                  if (!target.dataset.fallback) {
                    target.dataset.fallback = '1';
                    target.src = `https://www.google.com/s2/favicons?domain=${tool.domain}&sz=128`;
                  } else {
                    // Final fallback: show initial letter
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `<span class="text-lg font-bold text-gray-400">${tool.name.charAt(0)}</span>`;
                  }
                }} data-id="element-1821" />
                    </div>
                    <h3 className="font-poppins font-semibold text-lg mb-2 text-gray-900 group-hover:text-teal transition-colors" data-id="element-1822">
                      {tool.name}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed flex-1" data-id="element-1823">
                      {tool.desc}
                    </p>
                  </motion.div>)}
              </div>}
          </div>
        </div>
      </section>

      {/* Bottom CTA — stays dark */}
      <section className="py-24 px-6 relative overflow-hidden text-center bg-dark-bg mt-12" data-id="element-1824">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple/20 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1825" />

        <div className="relative z-10 max-w-2xl mx-auto" data-id="element-1826">
          <h2 className="font-poppins font-bold text-3xl md:text-4xl mb-6 text-white" data-id="element-1827">
            Don't see your tool?
          </h2>
          <p className="text-lg text-steel mb-10" data-id="element-1828">
            MISTR can build custom integrations for proprietary software, legacy
            systems, and niche industry tools.
          </p>
          <button className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-1829">
            Talk to MR
          </button>
        </div>
      </section>
    </main>;
}